import React, { useState, useEffect } from "react";
import { Button, Layout, Table, DatePicker, Select, Row, Col, Tooltip, Tabs } from "antd";
import axios from "axios";
import { LoadingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { getNeedToModelString, calcTotalModelsWorked, exportToCSV, generateExportFilename } from "../Utilities";
import ENVIRONMENT from "../../../environments";
import moment from "moment";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const DATE_FORMAT = "MM/DD/YYYY";

const { Header, Content, Footer } = Layout;

function AgencyDetails(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [filteredInvoiceData, setFilteredInvoiceData] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format(DATE_FORMAT)
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format(DATE_FORMAT)
  );
  // Non-approved items state
  const [nonApprovedData, setNonApprovedData] = useState([]);
  const [filteredNonApprovedData, setFilteredNonApprovedData] = useState([]);
  const [nonApprovedSummary, setNonApprovedSummary] = useState({});
  const [loadingNonApproved, setLoadingNonApproved] = useState(false);
  const [nonApprovedModelTypeFilter, setNonApprovedModelTypeFilter] = useState('');
  const [activeTab, setActiveTab] = useState('approved');
  // Approved items filtering state
  const [approvedModelTypeFilter, setApprovedModelTypeFilter] = useState('');

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      props.artistUsernames &&
      props.artistUsernames.length > 0
    ) {
      fetchArtistInvoice();
    }
  }, [startDate, endDate]);


  useEffect(() => {
    filterApprovedData();
  }, [approvedModelTypeFilter, invoiceData]);

  useEffect(() => {
    filterNonApprovedData();
  }, [nonApprovedModelTypeFilter, nonApprovedData]);

  const fetchArtistInvoice = () => {
    let payload = {
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
      artist_usernames: props.artistUsernames,
      artist_invoice: true,
    };
    console.log("payload = ", payload);
    setIsLoading(true);
    axios.post(ENVIRONMENT.GET_ARTIST_INVOICE, payload).then((res) => {
      console.log("invoicing data = ", res);
      setInvoiceData(res.data["invoice_data"]);
      setFilteredInvoiceData(res.data["invoice_data"]);
      setInvoiceSummary(res.data["summary"]);
      setIsLoading(false);
    });
  };

  const fetchNonApprovedData = () => {
    setLoadingNonApproved(true);
    axios.post(ENVIRONMENT.GET_ARTIST_NON_APPROVED_DATA, {
      artist_usernames: props.artistUsernames
    })
    .then(res => {
      setNonApprovedData(res.data['non_approved_items'] || []);
      setFilteredNonApprovedData(res.data['non_approved_items'] || []);
      setNonApprovedSummary(res.data['summary'] || {});
      setLoadingNonApproved(false);
    })
    .catch(() => setLoadingNonApproved(false));
  };

  
  const getTotalModelsWorked = (summary = invoiceSummary) => {
    let total_models = 0;
    total_models = (summary['spaces'] || 0) + (summary['space_variants'] || 0) + (summary['product_variants'] || 0) + (summary['products'] || 0) + (summary['materials'] || 0) + (summary['complaints'] || 0);
    if(!total_models)
      total_models = 0;
    return total_models;
  }

  const getBillingRateDisplay = () => {
    if (!props.agencyBillingRate || props.agencyBillingRate == 0 || props.agencyBillingRate == "") {
      return "N/A";
    }
    
    const baseRate = "$" + props.agencyBillingRate;
    const hourlyRate = props.agencyBillingType === "hourly" ? baseRate + " / hour" : "";
    const modelRate = props.agencyBillingType === "per_model" ? baseRate + " / model" : "";
    const variantRate = props.agencyBillingType === "per_model" ? "$" + (props.agencyBillingRate / 2).toFixed(1) + " / variant" : "";
    const materialRate = props.agencyBillingType === "per_model" ? "$" + (props.agencyBillingRate / 2).toFixed(1) + " / material" : "";
    
    return (
      <div>
        <p className="artist-details-value" style={{fontSize: 14, marginBottom: 7}}>
          {hourlyRate || modelRate || "N/A"}
        </p>
        {variantRate && <p className="artist-details-value" style={{fontSize: 14, marginBottom: 7}}>{variantRate}</p>}
        {materialRate && <p className="artist-details-value" style={{fontSize: 14, marginBottom: 7}}>{materialRate}</p>}
      </div>
    );
  }

  const onCalenderChange = (date_values) => {
    if (date_values && date_values[0] && date_values[1]) {
      setStartDate(date_values[0].format(DATE_FORMAT));
      setEndDate(date_values[1].format(DATE_FORMAT));
    }
  };

  const filterApprovedData = () => {
    let filtered_data = [];
    let filters = {};
    if(approvedModelTypeFilter != ''){
      filters['model_type'] = approvedModelTypeFilter;
    }
    filtered_data = invoiceData.filter(function(item) {
      for (var key in filters) {
        if (item[key] != filters[key])
          return false;
      }
      return true;
    });
    setFilteredInvoiceData(filtered_data);
  };

  const filterNonApprovedData = () => {
    let filtered_data = [];
    let filters = {};
    if(nonApprovedModelTypeFilter != ''){
      filters['model_type'] = nonApprovedModelTypeFilter;
    }
    filtered_data = nonApprovedData.filter(function(item) {
      for (var key in filters) {
        if (item[key] != filters[key])
          return false;
      }
      return true;
    });
    setFilteredNonApprovedData(filtered_data);
  };

  const handleTabChange = (activeKey) => {
    setActiveTab(activeKey);
    if (activeKey === 'nonapproved' && nonApprovedData.length === 0) {
      fetchNonApprovedData();
    }
  };

  const getSummarySection = (summary, isNonApproved = false) => (
    <Row style={{display: 'block'}}>
      <span className="manrope f-20">Invoice Summary</span>
      <Row className="artist-details-info" style={{marginBottom: 20, marginTop :20}}>
        <Col span={8}>
          <div className="manrope f-16">
            <p className="artist-details-heading">Total Hours Worked</p>
            <p className="artist-details-value">{summary['hours'] ? summary['hours'] + ' Hours' : '0 Hours'}</p>
          </div>
        </Col>

        <Col span={8}>
        <div className="manrope f-16">
              <p className="artist-details-heading">Total Models Made
              {getTotalModelsWorked(summary) > 0 ?
              <Tooltip title={<span className='manrope f-12 white'>
                {summary['spaces'] > 0 ?
                  <span style={{display: 'block', marginBottom: 1, marginTop: 1}}>Spaces: {summary['spaces']}</span> : ''
                }
                {summary['space_variants'] > 0 ?
                  <span style={{display: 'block', marginBottom: 1, marginTop: 1}}>Space Variants: {summary['space_variants']}</span> : ''
                }
                {summary['products'] > 0 ?
                  <span style={{display: 'block', marginBottom: 1, marginTop: 1}}>Products: {summary['products']}</span> : ''
                }
                {summary['product_variants'] > 0 ?
                  <span style={{display: 'block', marginBottom: 1, marginTop: 1}}>Product Variants: {summary['product_variants']}</span> : ''
                }
                {summary['complaints'] > 0 ?
                  <span style={{display: 'block', marginBottom: 1, marginTop: 1}}>Complaints: {summary['complaints']}</span> : ''
                }
                {summary['materials'] > 0 ?
                  <span style={{display: 'block', marginBottom: 1, marginTop: 1}}>Materials: {summary['materials']}</span> : ''
                }
              </span>}>
                <InfoCircleOutlined style={{marginLeft: 3}}/>
              </Tooltip> : ''}
              </p>
              <p className="artist-details-value">{getTotalModelsWorked(summary)}</p>
            </div>
        </Col>


        <Col span={8}>
          <div className="manrope f-16">
            <p className="artist-details-heading">{isNonApproved ? 'Total Amount Pending' : 'Total Amount Invoiced'}</p>
            <p className="artist-details-value">{summary['amount'] || '$0.0'}</p>
          </div>
        </Col>
      </Row>
    </Row>
  );

  const getCSVExportColumns = () => [
    {
      title: 'Model ID',
      dataIndex: 'model_id',
      key: 'model_id',
    },
    {
      title: 'Model Name',
      dataIndex: 'model_name',
      key: 'model_name',
    },
    {
      title: 'Model Type',
      dataIndex: 'model_type',
      key: 'model_type',
    },
    {
      title: 'Artist',
      dataIndex: 'artist_username',
      key: 'artist_username',
    },
    {
      title: 'Task Type',
      dataIndex: 'request_type',
      key: 'request_type',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Hours Worked',
      dataIndex: 'hours',
      key: 'hours',
    },
  ];

  const handleExportData = (exportType) => {
    const isApproved = exportType === 'approved';
    const filenamePrefix = isApproved ? 'Agency_Invoice' : 'Pending_Items';
    const data = isApproved ? filteredInvoiceData : filteredNonApprovedData;

    // Only append dates for approved data, not for non-approved
    const filename = isApproved
      ? generateExportFilename(`${filenamePrefix}_${props.artistUsernames.join('_')}`, startDate, endDate)
      : `${filenamePrefix}_${props.artistUsernames.join('_')}`;

    const columns = getCSVExportColumns();

    exportToCSV(filename, columns, data, {
      valueFormatter: (value, row, col) => {
        // Format specific columns for CSV export
        if (col.dataIndex === 'hours') {
          return parseFloat(value || 0);
        }
        if (col.dataIndex === 'model_type') {
          return `${value}${row['variant_of'] != null ? ' Variation' : ''}${getNeedToModelString(row)}`;
        }
        if (col.dataIndex === 'date') {
          return value || 'N/A';
        }
        return value;
      }
    });
  };

  const columns = [
    {
      title: <span className="manrope f-16 black-14">Model ID</span>,
      dataIndex: "model_id",
      key: 1,
      render: (text) => <span className="manrope f-14">{text}</span>,
    },
    {
      title: <span className="manrope f-16 black-14">Model Name</span>,
      dataIndex: "model_name",
      key: 2,
      render: (text) => <span className="manrope f-14 ">{text}</span>,
    },
    {
      title: <span className="manrope f-16 black-14">Model Type</span>,
      dataIndex: "model_type",
      key: 3,
      render: (text, index) => (
        <span className="manrope f-14">
          {text} {index["variant_of"] != null ? " Variation" : ""}{" "}
          {getNeedToModelString(index)}
        </span>
      ),
    },
    {
        title: <span className="manrope f-16 black-14">Artist</span>,
        dataIndex: "artist_username",
        key: 4,
        render: (text) => <span className="manrope f-14">{text}</span>,
      },
    {
      title: <span className="manrope f-16 black-14">Task Type</span>,
      dataIndex: "request_type",
      key: 5,
      render: (text) => <span className="manrope f-14">{text}</span>,
    },
    {
      title: <span className="manrope f-16 black-14">Date</span>,
      dataIndex: "date",
      key: 6,
      render: (text) => <span className="manrope f-14 ">{text || "N/A"}</span>,
    },
    {
      title: <span className="manrope f-16 black-14">Hours Worked</span>,
      dataIndex: "hours",
      key: 7,
      render: (text) => (
        <span className="manrope f-14">{parseFloat(text || 0) + " Hours"}</span>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className="content-container" style={{ marginTop: 20 }}>
        <Row>
          <Col span={24}>
            <div
              className="manrope f-24 w-700"
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                src="/img/back_arrow.png"
                onClick={() => props.setViewInvoice(false)}
                style={{ marginRight: 20, cursor: "pointer" }}
              />{" "}
              Agency Invoice
            </div>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Approved Invoices" key="approved">
            <Row style={{marginTop: 20, marginBottom: 10}}>
              <Col span={24} style={{display: 'flex', gap: 16, justifyContent: 'flex-end'}}>
                <RangePicker 
                  style={{marginRight: 8}} 
                  size={"large"} 
                  defaultValue={[moment(startDate, DATE_FORMAT), moment(endDate, DATE_FORMAT)]}
                  format={DATE_FORMAT} 
                  className="date-picker" 
                  popupStyle={{fontFamily: 'Manrope', fontSize: 14, color:"#40a9ff", background:"#e6efff" }} 
                  placeholder={['Date (From)', 'Date (To)']}
                  value={[moment(startDate, DATE_FORMAT), moment(endDate, DATE_FORMAT)]}
                  onCalendarChange={val => onCalenderChange(val)}
                />
                <Select 
                  placeholder="Model Type: All" 
                  size={"large"} 
                  style={{width: 180}}
                  value={approvedModelTypeFilter}
                  onChange={val => {
                    setApprovedModelTypeFilter(val);
                  }}
                >
                  <Option className="manrope f-14 black-55 select-view" value="">Model Type: All</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Space">Space</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Product">Product</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Material">Material</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Complaint">Complaint</Option>
                </Select>
                <Button 
                  className="save-button okay font-16"
                  onClick={() => handleExportData('approved')}
                  disabled={filteredInvoiceData.length === 0}
                  style={{color: 'white'}}
                >
                  Export CSV
                </Button>
              </Col>
            </Row>
            <Row style={{marginTop: 20}}>
              <Col span={24}>
                <Table
                  columns={columns}
                  dataSource={filteredInvoiceData}
                  bordered={true}
                  loading={{
                    indicator: <LoadingOutlined />,
                    spinning: isLoading,
                  }}
                />
              </Col>
            </Row>
            {getSummarySection(invoiceSummary, false)}
          </TabPane>
          
          <TabPane tab="Items Pending Approval" key="nonapproved">
            <Row style={{marginTop: 20, marginBottom: 10}}>
              <Col span={24} style={{display: 'flex', gap: 16, justifyContent: 'flex-end'}}>
                <Select 
                  placeholder="Model Type: All" 
                  size={"large"} 
                  style={{width: 180}}
                  value={nonApprovedModelTypeFilter}
                  onChange={val => {
                    setNonApprovedModelTypeFilter(val);
                  }}
                >
                  <Option className="manrope f-14 black-55 select-view" value="">Model Type: All</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Space">Space</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Product">Product</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Material">Material</Option>
                  <Option className="manrope f-14 black-55 select-view" value="Complaint">Complaint</Option>
                </Select>
                <Button 
                  onClick={() => handleExportData('nonapproved')}
                  disabled={filteredNonApprovedData.length === 0}
                  className="save-button okay font-16"
                  style={{color: 'white'}}
                >
                  Export CSV
                </Button>
              </Col>
            </Row>
            <Row style={{marginTop: 20}}>
              <Col span={24}>
                <Table
                  columns={columns}
                  dataSource={filteredNonApprovedData}
                  bordered={true}
                  loading={{
                    indicator: <LoadingOutlined />,
                    spinning: loadingNonApproved,
                  }}
                />
              </Col>
            </Row>
            {getSummarySection(nonApprovedSummary, true)}
          </TabPane>
        </Tabs>
      </div>
    </React.Fragment>
  );
}

export default AgencyDetails;
